import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { toValidObjectIdString, formatApiDate } from '../utils/security';
import {
  fetchSiparisler,
  fetchSiparisDetay,
  fetchSiparisSekmeSayilari,
  teknisyenOnaylaSiparis,
  teknisyenIptalSiparis,
} from '../services/siparisService';
import SiparisDetayModal from '../components/SiparisDetayModal';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const getSiparisId = (s) => toValidObjectIdString(s?.id ?? s?._id) || s?.siparisNumarasi || '';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const getSiparisTipLabel = (s) => {
  if (s?.konsinye) return 'Konsinye';
  if (s?.garantiDahilinde) return 'Garanti dahilinde';
  return 'Doğrudan Sipariş';
};

const getOlusturanLabel = (s) => {
  const u = s?.olusturanKullaniciId;
  if (!u || typeof u === 'string') return '—';
  const ad = [u.ad, u.soyad].filter(Boolean).join(' ').trim();
  return ad || u.kullaniciAdi || '—';
};

const TechServiceSiparisOnaylariPage = () => {
  const { confirm } = useConfirmDialog();
  const goBack = useBackRoute('/');
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [bekleyenSayisi, setBekleyenSayisi] = useState(0);
  const [selectedSiparis, setSelectedSiparis] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const refreshSekmeSayilari = useCallback(async () => {
    try {
      const response = await fetchSiparisSekmeSayilari();
      if (response?.basarili && response?.veri) {
        setBekleyenSayisi(response.veri['teknisyen-onay-bekleyen'] ?? 0);
      }
    } catch {
      setBekleyenSayisi(0);
    }
  }, []);

  const loadSiparisler = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchSiparisler({
        tab: 'teknisyen-onay-bekleyen',
        sayfa: currentPage,
        limit: PAGE_DEFAULT_LIMIT,
      });
      if (response?.basarili && response?.veri) {
        setSiparisler(response.veri.siparisler || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Siparişler yüklenemedi'));
      setSiparisler([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadSiparisler();
  }, [loadSiparisler]);

  useEffect(() => {
    refreshSekmeSayilari();
  }, [refreshSekmeSayilari]);

  const handleViewDetail = async (siparis) => {
    const id = getSiparisId(siparis);
    if (!id) {
      window.showNotification?.('error', 'Sipariş ID alınamadı');
      return;
    }
    try {
      const response = await fetchSiparisDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedSiparis(response.veri);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  const handleOnayla = async (siparis) => {
    const id = getSiparisId(siparis);
    if (!id) return;
    const ok = await confirm({
      title: 'Siparişi onayla',
      message: `${siparis.siparisNumarasi} numaralı teknisyen siparişi admin onay kuyruğuna alınsın mı?`,
      variant: 'info',
      confirmLabel: 'Onayla',
    });
    if (!ok) return;
    setActionLoading(id);
    try {
      const response = await teknisyenOnaylaSiparis(id);
      if (response?.basarili) {
        window.showNotification?.('success', 'Sipariş admin onay kuyruğuna alındı');
        loadSiparisler();
        refreshSekmeSayilari();
        setDetailModalOpen(false);
        setSelectedSiparis(null);
      } else {
        window.showNotification?.('error', response?.mesaj || 'İşlem başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İşlem başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleIptal = async (siparis) => {
    const id = getSiparisId(siparis);
    if (!id) return;
    const ok = await confirm({
      title: 'Siparişi iptal et',
      message: `${siparis.siparisNumarasi} numaralı teknisyen siparişi iptal edilsin mi? Admin panelinde görünmez.`,
      variant: 'danger',
      confirmLabel: 'İptal et',
    });
    if (!ok) return;
    setActionLoading(id);
    try {
      const response = await teknisyenIptalSiparis(id);
      if (response?.basarili) {
        window.showNotification?.('success', 'Sipariş iptal edildi');
        loadSiparisler();
        refreshSekmeSayilari();
        setDetailModalOpen(false);
        setSelectedSiparis(null);
      } else {
        window.showNotification?.('error', response?.mesaj || 'İşlem başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İşlem başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <main className="main-content tech-service-siparis">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Sipariş Onayları</h1>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Siparişler yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : siparisler.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-clipboard-check" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Onay bekleyen teknisyen siparişi yok</div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Sipariş No</th>
                    <th>Tarih</th>
                    <th>Tip</th>
                    <th>Oluşturan</th>
                    <th>Servis Kaydı</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {siparisler.map((s, idx) => {
                    const id = getSiparisId(s);
                    return (
                      <tr key={id || idx}>
                        <td style={{ fontWeight: 500 }}>{s.siparisNumarasi}</td>
                        <td>{formatApiDate(s.olusturmaTarihi)}</td>
                        <td>{getSiparisTipLabel(s)}</td>
                        <td>{getOlusturanLabel(s)}</td>
                        <td>
                          {s.servisKaydiId ? (
                            <Link
                              to={`/service-records/${s.servisKaydiId}/update`}
                              style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
                            >
                              {s.servisKaydiNumarasi || s.servisKaydiId}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="table-actions islemler-sutunu">
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            onClick={() => handleViewDetail(s)}
                            title="Detay"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            onClick={() => handleOnayla(s)}
                            title="Onayla"
                            disabled={actionLoading === id}
                            aria-label="Onayla"
                          >
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            type="button"
                            className="category-action-btn delete-btn"
                            onClick={() => handleIptal(s)}
                            title="İptal"
                            disabled={actionLoading === id}
                            aria-label="İptal"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {detailModalOpen && selectedSiparis && (
        <SiparisDetayModal
          siparis={selectedSiparis}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedSiparis(null);
          }}
          onRefresh={loadSiparisler}
          isAdmin={false}
        />
      )}
    </main>
  );
};

export default TechServiceSiparisOnaylariPage;
